import { EnvType } from "../enums";

export class ENV {
    //--- TEMPORARY ---
    static readonly DEV_MODE: boolean = envAudit("DEV_MODE", EnvType.Boolean);

    //--- GENERAL ---
    static readonly VERSION: number = envAudit("VERSION", EnvType.Number);

    static readonly MAIL: string = envAudit("MAIL", EnvType.String);
    static readonly NUMBER: string = envAudit("NUMBER", EnvType.String);
    static readonly ADR_NAME: string = envAudit("ADR_NAME", EnvType.String);
    static readonly ADR_STREET: string = envAudit("ADR_STREET", EnvType.String);
    static readonly ADR_CITY: string = envAudit("ADR_CITY", EnvType.String);
    static readonly ADR_COUNTRY: string = envAudit("ADR_COUNTRY", EnvType.String);

    static readonly ADR_B_NAME: string = envAudit("ADR_B_NAME", EnvType.String);
    static readonly ADR_B_STREET: string = envAudit("ADR_B_STREET", EnvType.String);
    static readonly ADR_B_CITY: string = envAudit("ADR_B_CITY", EnvType.String);
    static readonly ADR_B_COUNTRY: string = envAudit("ADR_B_COUNTRY", EnvType.String);

    //--- FRONTEND ---
    static readonly FRONTEND_HOST: string = envAudit("FRONTEND_HOST", EnvType.String);
    static readonly FRONTEND_PORT: number | undefined = envAudit("FRONTEND_PORT", EnvType.Number, { optional: true });
    static readonly FRONTEND_PATH: string | undefined = envAudit("FRONTEND_PATH", EnvType.String, { optional: true });
    static readonly FRONTEND_WWW: boolean = envAudit("FRONTEND_WWW", EnvType.Boolean);
    static readonly FRONTEND_HTTPS: boolean = envAudit("FRONTEND_HTTPS", EnvType.Boolean);

    //--- CDN ---
    static readonly CDN_HOST: string = envAudit("CDN_HOST", EnvType.String);
    static readonly CDN_PORT: number | undefined = envAudit("CDN_PORT", EnvType.Number, { optional: true });
    static readonly CDN_PATH: string | undefined = envAudit("CDN_PATH", EnvType.String, { optional: true });
    static readonly CDN_WWW: boolean = envAudit("CDN_WWW", EnvType.Boolean);
    static readonly CDN_HTTPS: boolean = envAudit("CDN_HTTPS", EnvType.Boolean)
    static readonly CDN_TOKEN: string = envAudit("CDN_TOKEN", EnvType.String);
}

export const getFrontendOrigin = (): string => {
    const protocol = ENV.FRONTEND_HTTPS && !ENV.DEV_MODE ? "https://" : "http://";
    const www = ENV.FRONTEND_WWW ? "www." : "";
    const host = ENV.FRONTEND_HOST;
    const port = ENV.FRONTEND_PORT ? `:${ENV.FRONTEND_PORT}` : "";
    const path = ENV.FRONTEND_PATH ? `/${ENV.FRONTEND_PATH}` : "";

    return `${protocol}${www}${host}${port}${path}`;
}

export const getCDNOrigin = (): string => {
    const protocol = ENV.CDN_HTTPS && !ENV.DEV_MODE ? "https://" : "http://";
    const www = ENV.CDN_WWW ? "www." : "";
    const host = ENV.CDN_HOST;
    const port = ENV.CDN_PORT ? `:${ENV.CDN_PORT}` : "";
    const path = ENV.CDN_PATH ? `/${ENV.CDN_PATH}` : "";

    return `${protocol}${www}${host}${port}${path}`;
}

/**
 * Environment value audit
 * @param key Key-Name ("REACT_APP_" will be added automatically)
 * @param type Variable type
 * @param specifications Further specifications -- optional; exactLength (optional)
 * @returns undefined, string, number or boolean
 */
function envAudit(
    key: string,
    type: EnvType,
    specifications?: {
        optional?: boolean, //Optional value
        exactLength?: number, //String exaclt length
        min?: number //Min number
    }
): any { //Needs to be any because some values require numbers, boolean etc. exclusively
    const value = process.env[`${"REACT_APP_"}${key}`];

    let convertedValue: undefined | string | number | boolean = undefined;
    switch (type) {
        case EnvType.String:
            if (value !== undefined && value.trim().length === 0) { //Values with spaces only will be undefined
                convertedValue = undefined;
                break;
            }
            convertedValue = value;
            break;
        case EnvType.Boolean: //"Boolean(JSON.prase())" will not be used because 0 & 1 could also be true/false
            if (value === "true") {
                convertedValue = true;
            } else if (value === "false") {
                convertedValue = false;
            } else {
                return console.error(`Environment variable ${key} needs to be a ${type}.`);
            }
            break;
        case EnvType.Number:
            if (!isNaN(Number(value))) {
                convertedValue = Number(value);
            } else {
                return console.error(`Environment variable ${key} needs to be a ${type}.`);
            }
            break;
        default: break;
    }

    if (!specifications && convertedValue === undefined) { //Non-optional value missing
        return console.error(`Environment variable ${key} is missing.`);
    }

    if (specifications) {
        //Optional check
        if (specifications.optional && convertedValue === undefined) {
            return;
        }
        
        //exactLength
        if (specifications.exactLength) {
            if (type !== EnvType.String) {
                return console.error(`Environment variable ${key} can not contain "exactLength" property without being a string.`);
            }
            if (typeof convertedValue !== "string") {
                return console.error(`Environment variable ${key} needs to be a string if specification contains "exactLength" element.`);
            }
            if (convertedValue.length !== specifications.exactLength) {
                return console.error(`Environment variable ${key} needs to be ${specifications.exactLength} characters long.`);
            }
        }
        
        //min
        if (specifications.min) {
            if (type !== EnvType.Number) {
                return console.error(`Environment variable ${key} can not contain "min" property without being a number.`);
            }
            if (typeof convertedValue !== "number") {
                return console.error(`Environment variable ${key} needs to be a number if specification contains "min" element.`);
            }
            if (convertedValue < specifications.min) {
                return console.error(`Environment variable ${key} needs to be bigger or equal to ${specifications.min}.`);
            }
        }
    }

    return convertedValue;
}